<script setup lang="ts">
import { ref, shallowRef, watch } from 'vue'
import { trans } from '@/munio/i18n/index.js'
import MdlTextfield from './Textfield.vue'
import MdlButton from './Button.vue'
import { bytesForHumans } from '@/munio/utils'

defineOptions({
  name: 'MdlFile',
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    name?: string
    file?: File | { name: string; size?: number; type?: string }
    outlined?: boolean
    opaque?: boolean
    accept?: string
    disabled?: boolean
    error?: boolean | string
  }>(),
  {
    outlined: true,
    opaque: true,
    disabled: false,
  },
)

const emit = defineEmits<{
  (e: 'change', file: File): void
  (e: 'update:file', file: File): void
}>()

const $file = ref<HTMLInputElement>()
const input = shallowRef<File | undefined>()

function onSelect(event: Event) {
  input.value = (event.target as HTMLInputElement).files![0] as File
  emit('change', input.value)
  emit('update:file', input.value)
}

watch(
  () => props.file,
  (newVal) => {
    if (newVal instanceof File) {
      input.value = newVal
    }
  },
  { immediate: true },
)
</script>

<template>
  <div class="mdl-file">
    <input
      ref="$file"
      type="file"
      :name="name"
      :accept="accept"
      :disabled="disabled"
      v-bind="$attrs"
      @change="onSelect"
    />

    <MdlButton v-bind="$attrs" :outlined="outlined" :opaque="opaque" :disabled="disabled" @click="$file?.click()">
      {{ trans('Choose file') }}
    </MdlButton>

    <MdlTextfield
      readonly
      compact
      :model-value="input?.name || file?.name"
      :error="error"
      :disabled="disabled"
      :placeholder="trans('No file selected')"
      @click="$file?.click()"
    >
      <span v-if="input" class="opaque nowrap">{{ bytesForHumans(input.size) }}</span>
    </MdlTextfield>
  </div>
</template>
